import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { createIdea, diagnose, getUsageLimits } from "../lib/api";
import AppShell from "./AppShell";
import { track } from "@/lib/track";

const FIELDS = [
  {
    k: "title",
    label: "What's it called?",
    placeholder: "Working title. One word or two.",
    kind: "input",
    hint: "Optional honesty: this is just a label, not the pitch.",
  },
  {
    k: "one_liner",
    label: "In one sentence — what is it?",
    placeholder: "e.g. Calendly for founder pipelines.",
    kind: "input",
    hint: "If you can't say it in a sentence, that's a signal.",
  },
  {
    k: "problem",
    label: "What problem are you solving?",
    placeholder:
      "Describe the specific pain. Who feels it, when, how often, and how much does it cost them today?",
    kind: "textarea",
    hint: "Be specific. 'Productivity' is not a problem.",
  },
  {
    k: "target_user",
    label: "Who exactly feels this pain?",
    placeholder: "e.g. Solo founders, pre-seed, 0-3 FTEs, B2B SaaS, US-based.",
    kind: "input",
    hint: "The narrower, the better. 'Everyone' = no one.",
  },
  {
    k: "access_to_users",
    label: "How will you reach them?",
    placeholder:
      "Existing network, communities, lists, cold outbound… be concrete.",
    kind: "textarea",
    hint: "If you can't reach 10 of them by Friday, we have a problem.",
  },
  {
    k: "domain_experience",
    label: "Your honest domain experience",
    placeholder:
      "Years in the space, companies, roles, side projects, painful firsthand experience with this problem.",
    kind: "textarea",
    hint: "Don't puff this up. I'll notice.",
  },
  {
    k: "motivation",
    label: "Why you? Why now?",
    placeholder:
      "What's the real reason you want to build this? (Money, obsession, revenge, boredom — be honest.)",
    kind: "textarea",
    hint: "I'll ask this again in week 6.",
  },
  {
    k: "extra_context",
    label: "Anything else I should know?",
    placeholder:
      "Competitors you already know about, a unique insight, prior traction, constraints.",
    kind: "textarea",
    optional: true,
  },
];

function formatError(detail, fallback = "Something broke. Try again.") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (detail.category && detail.reason) {
    return `Rejected: ${detail.category.replace(/_/g, " ")}. ${detail.reason}`;
  }
  if (detail.reason) return detail.reason;
  if (detail.msg) return detail.msg;
  if (detail?.unlimited) return detail.message || fallback;
  if (detail?.code === "diagnosis_weekly_limit")
    return `Weekly limit reached (${detail.weekly_used}/${detail.weekly_limit}). ${detail.message}`;
  return fallback;
}

function LimitState({ limits, onRetry, onDashboard, onProfile }) {
  const used = limits?.weekly_used ?? 0;
  const total = limits?.weekly_limit ?? 0;
  const remaining = Math.max(total - used, 0);
  const percent = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const blocked = !limits?.unlimited && total > 0 && used >= total;

  if (!blocked) {
    return (
      <AppShell>
        <div className="flex-1 flex items-center justify-center">
          <div className="card p-8 sm:p-12 max-w-2xl w-full fade-up">
            <div className="overline mb-3">limits check</div>
            <h1 className="font-display text-3xl sm:text-5xl font-semibold tracking-tighter leading-[1.05] mb-4">
              Couldn&apos;t verify your usage.
            </h1>
            <p className="text-zinc-600 text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
              The intake is ready, but the limit check didn&apos;t come back.
              Try again in a moment.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button className="btn btn-primary" onClick={onRetry}>
                Check again
              </button>
              <button className="btn btn-secondary" onClick={onDashboard}>
                Back to ideas
              </button>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex-1 flex items-center justify-center">
        <div className="card p-8 sm:p-12 max-w-3xl w-full fade-up">
          <div className="overline mb-3">weekly limit reached</div>
          <h1 className="font-display text-3xl sm:text-5xl font-semibold tracking-tighter leading-[1.05] mb-4">
            You&apos;ve used this week&apos;s diagnoses.
          </h1>
          <p className="text-zinc-600 text-base sm:text-lg leading-relaxed mb-8 max-w-2xl">
            You can still review past verdicts and sharpen an existing idea. New
            diagnoses reopen when your weekly allowance refreshes.
          </p>

          <div className="border border-zinc-200 rounded-md bg-zinc-50 p-5 mb-8">
            <div className="flex items-end justify-between gap-4 mb-3">
              <div>
                <div className="overline mb-1">diagnoses used</div>
                <div className="font-display text-3xl font-semibold tracking-tight">
                  {used}/{total}
                </div>
              </div>
              <div className="text-right">
                <div className="overline mb-1">remaining</div>
                <div className="font-display text-3xl font-semibold tracking-tight text-zinc-400">
                  {remaining}
                </div>
              </div>
            </div>
            <div className="h-2 rounded-sm bg-white border border-zinc-200 overflow-hidden">
              <div
                className="h-full bg-zinc-950"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button className="btn btn-primary" onClick={onDashboard}>
              Review ideas
            </button>
            <button className="btn btn-secondary" onClick={onProfile}>
              View usage
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function LoadingState() {
  return (
    <AppShell>
      <div className="flex-1 flex items-center justify-center">
        <div className="card p-8 sm:p-12 max-w-2xl w-full fade-up">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 border-2 border-zinc-950 border-t-transparent rounded-full spin" />
            <div>
              <div className="overline mb-1">limits check</div>
              <div className="text-sm text-zinc-500">
                Making sure there&apos;s room for one more verdict.
              </div>
            </div>
          </div>
          <div className="h-2 rounded-sm bg-zinc-100 overflow-hidden">
            <div className="h-full w-1/3 bg-zinc-950" />
          </div>
        </div>
      </div>
    </AppShell>
  );
}

export default function NewIdea() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [state, setState] = useState({
    title: "",
    one_liner: "",
    problem: "",
    target_user: "",
    access_to_users: "",
    domain_experience: "",
    motivation: "",
    extra_context: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [hasStarted, setHasStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [limits, setLimits] = useState(null);

  const field = FIELDS[step];
  const val = state[field.k] || "";
  const canNext = field.optional || val.trim().length > 0;
  const isLast = step === FIELDS.length - 1;

  const loadLimits = useCallback(() => {
    setLoading(true);
    getUsageLimits()
      .then((data) => setLimits(data.diagnosis))
      .catch(() => setLimits(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadLimits();
  }, [loadLimits]);

  const go = async () => {
    if (!canNext) {
      setError("This one's required.");
      return;
    }
    setError("");
    if (!isLast) {
      setStep((s) => s + 1);
      return;
    }
    setSubmitting(true);
    try {
      track("idea_submitted");
      const idea = await createIdea(state);
      const job = await diagnose(idea.idea_id);
      navigate(`/ideas/${idea.idea_id}?job=${job.job_id}`, { replace: true });
    } catch (e) {
      setError(formatError(e?.response?.data?.detail));
      setSubmitting(false);
    }
  };

  const onKey = (e) => {
    const el = e.target;

    if (e.key === "Enter" && !e.shiftKey && field.kind !== "textarea") {
      e.preventDefault();
      go();
    }

    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      go();
    }

    if (
      (e.metaKey || e.ctrlKey) &&
      e.key === "ArrowLeft" &&
      el.selectionStart === 0
    ) {
      e.preventDefault();
      if (step > 0) setStep((s) => s - 1);
    }

    if (
      (e.metaKey || e.ctrlKey) &&
      e.key === "ArrowRight" &&
      el.selectionStart === el.value.length
    ) {
      e.preventDefault();
      if (el.value.trim().length === 0 && !field.optional) {
        setError("This one's required.");
        return;
      }
      if (step < FIELDS.length - 1) setStep((s) => s + 1);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  if (!limits || (!limits.unlimited && limits.weekly_used >= limits.weekly_limit)) {
    return (
      <LimitState
        limits={limits}
        onRetry={loadLimits}
        onDashboard={() => navigate("/dashboard")}
        onProfile={() => navigate("/profile")}
      />
    );
  }

  return (
    <AppShell>
      <Helmet>
        <title>Validate Your Startup Idea | Killswitch</title>
        <meta
          name="description"
          content="Submit your startup idea and get a brutally honest AI verdict with actionable steps."
        />
      </Helmet>
      <div className="w-[700px] mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div className="overline" data-testid="intake-progress">
            Step {String(step + 1).padStart(2, "0")} /{" "}
            {String(FIELDS.length).padStart(2, "0")}
          </div>
          <div className="flex gap-1">
            {FIELDS.map((_, i) => (
              <div
                key={i}
                className={`h-[2px] w-8 ${i <= step ? "bg-zinc-950" : "bg-zinc-200"}`}
              />
            ))}
          </div>
        </div>

        <div key={field.k} className="fade-up">
          <h1 className="font-display text-3xl sm:text-5xl font-semibold tracking-tighter leading-[1.05] mb-4">
            {field.label}
          </h1>
          {field.hint && (
            <p className="text-zinc-500 mb-8 text-lg font-serif italic">
              {field.hint}
            </p>
          )}

          {field.kind === "input" ? (
            <input
              autoFocus
              data-testid={`intake-input-${field.k}`}
              className="w-full bg-transparent border-b-2 border-zinc-200 focus:border-zinc-950 outline-none py-4 font-display text-2xl sm:text-3xl tracking-tight placeholder:text-zinc-300 transition-colors"
              placeholder={field.placeholder}
              value={val}
              onChange={(e) =>
                setState({ ...state, [field.k]: e.target.value })
              }
              onKeyDown={onKey}
              onFocus={() => {
                if (!hasStarted) {
                  track("input_started");
                  setHasStarted(true);
                }
              }}
            />
          ) : (
            <textarea
              autoFocus
              data-testid={`intake-input-${field.k}`}
              rows={5}
              className="w-full bg-zinc-50 border border-zinc-200 focus:border-zinc-950 focus:bg-white outline-none rounded-md p-4 text-base leading-relaxed placeholder:text-zinc-400 transition-colors resize-none"
              placeholder={field.placeholder}
              value={val}
              onChange={(e) =>
                setState({ ...state, [field.k]: e.target.value })
              }
              onKeyDown={onKey}
              onFocus={() => {
                if (!hasStarted) {
                  track("input_started");
                  setHasStarted(true);
                }
              }}
            />
          )}

          {error && (
            <div
              className="mt-4 text-sm text-red-600 font-medium shake"
              data-testid="intake-error"
            >
              {error}
            </div>
          )}

          <div className="mt-10 flex items-center justify-between">
            <button
              data-testid="intake-back-btn"
              className="btn btn-ghost"
              disabled={step === 0 || submitting}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
            >
              ← Back
            </button>

            <div className="flex items-center gap-3">
              {field.optional && (
                <button
                  className="btn btn-ghost text-xs"
                  onClick={() => {
                    if (isLast) {
                      go();
                      return;
                    }
                    setStep((s) => s + 1);
                  }}
                  data-testid="intake-skip-btn"
                >
                  skip
                </button>
              )}
              <button
                data-testid="intake-next-btn"
                className="btn btn-primary"
                onClick={go}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full" />{" "}
                    Diagnosing…
                  </>
                ) : isLast ? (
                  "Diagnose →"
                ) : (
                  "Next →"
                )}
              </button>
            </div>
          </div>

          <div className="mt-6 text-xs font-mono uppercase tracking-[0.15em] text-zinc-400">
            {field.kind === "textarea"
              ? "⌘/Ctrl + Enter to continue"
              : "↵ to continue"}
          </div>
          <div className="mt-6 text-xs font-mono uppercase tracking-[0.15em] text-zinc-400">
            {"⌘/Ctrl + ← → to navigate"}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
